import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Avatar, Select,
  MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { CardGiftcard as RewardIcon } from '@mui/icons-material';
import api from '../../services/api';

interface DistPeriod {
  periodo: string;
  total_funcionarios: number;
  total_valor: number;
  concluidos: number;
  falhas: number;
  data_inicio: string;
  data_fim: string;
}

const fmt = (v: number) => v.toLocaleString('pt-CV');

const fmtDate = (d: string) => {
  try { return new Date(d).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const CorporatePortalDistributions: React.FC = () => {
  const [periods, setPeriods]   = useState<DistPeriod[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [filter, setFilter]     = useState('');

  useEffect(() => {
    api.get('/portal/corporate/distributions', { params: filter ? { periodo: filter } : {} })
      .then(r => setPeriods(r.data.data || []))
      .catch(() => setError('Erro ao carregar distribuições'))
      .finally(() => setLoading(false));
  }, [filter]);

  const totalDistribuido = periods.reduce((s, p) => s + Number(p.total_valor), 0);

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: '#6233A0' }}><RewardIcon /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Distribuições</Typography>
          <Typography variant="body2" color="text.secondary">
            Histórico de recompensas distribuídas aos colaboradores
          </Typography>
        </Box>
      </Box>

      {periods.length > 0 && (
        <Box display="flex" gap={3} mb={3} flexWrap="wrap">
          <Paper sx={{ p: 2, minWidth: 160 }}>
            <Typography variant="h4" fontWeight={700} color="#6233A0">{periods.length}</Typography>
            <Typography variant="caption" color="text.secondary">Períodos</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="h4" fontWeight={700} color="#10B981">{fmt(totalDistribuido)} ECV</Typography>
            <Typography variant="caption" color="text.secondary">Total distribuído</Typography>
          </Paper>
        </Box>
      )}

      <FormControl size="small" sx={{ mb: 2, minWidth: 180 }}>
        <InputLabel>Período</InputLabel>
        <Select value={filter} label="Período" onChange={e => setFilter(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {periods.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.periodo}</MenuItem>)}
        </Select>
      </FormControl>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><b>Período</b></TableCell>
                <TableCell align="right"><b>Funcionários</b></TableCell>
                <TableCell align="right"><b>Total (ECV)</b></TableCell>
                <TableCell align="center"><b>Concluídos</b></TableCell>
                <TableCell align="center"><b>Falhas</b></TableCell>
                <TableCell><b>Data</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma distribuição encontrada
                </TableCell></TableRow>
              ) : periods.map(p => (
                <TableRow key={p.periodo} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{p.periodo}</Typography>
                  </TableCell>
                  <TableCell align="right">{p.total_funcionarios}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={700} color="#10B981">{fmt(Number(p.total_valor))}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={p.concluidos} size="small" color="success" />
                  </TableCell>
                  <TableCell align="center">
                    {p.falhas > 0
                      ? <Chip label={p.falhas} size="small" color="error" />
                      : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDate(p.data_inicio)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CorporatePortalDistributions;
